/**
 * Phase 4 — Near-Miss detail. View + corrective-actions table + close (approve-gated).
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, Plus, CloudOff } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import AuthImage from '../../components/safety/AuthImage';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { toast } from 'sonner';

const SEV_TONE = {
  low: 'bg-slate-100 text-slate-700 border-slate-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  critical: 'bg-red-50 text-red-700 border-red-200',
};
import { formatDateTime as fmt } from '../../lib/format';

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">{label}</div>
    <div className="text-sm text-slate-800 whitespace-pre-wrap">{value || '—'}</div>
  </div>
);

export default function NearMissDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [closing, setClosing] = useState(false);
  const [newAction, setNewAction] = useState({ action: '', owner: '', due_date: '' });

  const load = () => apiGet(`/safety/near-miss/${id}`).then(setDoc).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const saveActions = async (actions) => {
    try {
      await apiPut(`/safety/near-miss/${id}`, { corrective_actions: actions, status: 'actions_pending' });
      load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'No permission to edit' : 'Failed to update');
    }
  };

  const addAction = () => {
    if (!newAction.action.trim()) return;
    const actions = [...(doc.corrective_actions || []), { ...newAction, done: false }];
    saveActions(actions);
    setNewAction({ action: '', owner: '', due_date: '' });
  };

  const toggleAction = (idx) => {
    const actions = (doc.corrective_actions || []).map((a, i) => (i === idx ? { ...a, done: !a.done } : a));
    saveActions(actions);
  };

  const close = async () => {
    setClosing(true);
    try {
      await apiPost(`/safety/near-miss/${id}/close`, {});
      toast.success('Near-miss closed');
      load();
    } catch (e) {
      if (e?.response?.status === 403) toast.error('You do not have permission to close near-misses.');
      else toast.error(e?.response?.data?.detail || 'Failed to close');
    } finally {
      setClosing(false);
    }
  };

  if (!doc) return <div className="text-center text-xs text-slate-400 py-16 uppercase tracking-widest font-display">Loading…</div>;
  const isClosed = doc.status === 'closed';

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate('/app/safety')} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-3" data-testid="nm-back"><ArrowLeft size={14} /> Back to Safety</button>
      <PageHeader overline="Near-Miss Incident" title="Near-Miss"
        actions={<span className={`tag border ${SEV_TONE[doc.potential_severity]}`}>Potential: {doc.potential_severity}</span>} />

      <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5" data-testid="near-miss-detail">
        {doc.offline_queued_at && (
          <div data-testid="nm-offline-audit" className="flex items-center gap-2 text-[12px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-sm">
            <CloudOff size={13} /> Filed offline on-site at <b>{fmt(doc.offline_queued_at)}</b> — synced later.
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Status" value={(doc.status || '').replace('_', ' ')} />
          <Field label="Incident Time" value={doc.incident_time ? fmt(doc.incident_time) : '—'} />
          <Field label="Location" value={doc.location_on_site} />
          <Field label="Reported By" value={`${doc.raised_by} · ${fmt(doc.raised_at)}`} />
        </div>
        <Field label="What happened" value={doc.description} />
        <Field label="What could have happened" value={doc.what_could_have_happened} />
        <Field label="Actual consequence" value={doc.actual_consequence} />
        <Field label="Root cause" value={doc.root_cause} />

        {(doc.contributing_factors || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Contributing factors</div>
            <div className="flex flex-wrap gap-1.5">
              {doc.contributing_factors.map((f, i) => <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-sm">{f}</span>)}
            </div>
          </div>
        )}

        {(doc.photos || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Photos ({doc.photos.length})</div>
            <div className="flex flex-wrap gap-2">
              {doc.photos.map((p) => <AuthImage key={p.id} src={p.image_url} alt="near-miss" className="w-28 h-28 object-cover rounded-sm border border-slate-200" />)}
            </div>
          </div>
        )}

        {/* Corrective actions */}
        <div className="border-t border-slate-200 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Corrective Actions</div>
          <table className="w-full dense-table mb-2" data-testid="nm-actions-table">
            <thead><tr><th></th><th>Action</th><th>Owner</th><th>Due</th></tr></thead>
            <tbody>
              {(doc.corrective_actions || []).length === 0 ? <tr><td colSpan={4} className="text-xs text-slate-400 py-3 text-center">No corrective actions yet.</td></tr>
                : doc.corrective_actions.map((a, i) => (
                  <tr key={i}>
                    <td><input type="checkbox" checked={!!a.done} onChange={() => toggleAction(i)} disabled={isClosed} data-testid={`nm-action-done-${i}`} /></td>
                    <td className={`text-sm ${a.done ? 'line-through text-slate-400' : ''}`}>{a.action}</td>
                    <td className="text-xs text-slate-600">{a.owner || '—'}</td>
                    <td className="text-xs text-slate-600">{a.due_date || '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!isClosed && (
            <div className="flex flex-wrap gap-2">
              <input value={newAction.action} onChange={(e) => setNewAction({ ...newAction, action: e.target.value })} placeholder="Corrective action" className="flex-1 min-w-[160px] px-2 py-1.5 border border-slate-300 rounded-sm text-sm" data-testid="nm-new-action" />
              <input value={newAction.owner} onChange={(e) => setNewAction({ ...newAction, owner: e.target.value })} placeholder="Owner" className="w-28 px-2 py-1.5 border border-slate-300 rounded-sm text-sm" />
              <input type="date" value={newAction.due_date} onChange={(e) => setNewAction({ ...newAction, due_date: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm" />
              <button onClick={addAction} className="px-3 py-1.5 bg-slate-100 rounded-sm hover:bg-slate-200" data-testid="nm-add-action-btn"><Plus size={15} /></button>
            </div>
          )}
        </div>

        {!isClosed && (
          <div className="border-t border-slate-200 pt-4">
            <button onClick={close} disabled={closing} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="nm-close-btn">
              {closing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Close Near-Miss
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
